import { useOptimisticUpdate } from "@common/hooks/useOptimisticUpdate";
import { useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { EvaluationStatus } from "@common/components/questionnaireComponents/evaluateQuestionnaireDialog/EvaluateQuestionnaireDialog.types";
import { useUrlProvider } from "../../../useUrlProvider";
import { EvaluatedTaskViewModel, TaskEvaluationSaveModel, TaskViewModel } from "../TaskPaneContent.types";
import { getTaskQueryKey, putTaskEvaluation, toggleEvaluationStatus } from "../TaskPaneContent.hooks";

export interface IEvaluatedTaskSection {
    evaluationTask: TaskViewModel;
    evaluatedTask: EvaluatedTaskViewModel;
}

export function useEvaluatedTaskSection(props: IEvaluatedTaskSection) {
    const queryClient = useDotLegalQueryClient();
    const optimisticUpdate = useOptimisticUpdate();
    const { getTaskManagementTaskDialogUrl } = useUrlProvider();

    const evaluationUpdater = {
        onEvaluationStatusChange: (evaluationStatus: EvaluationStatus) =>
            saveEvaluation({ ...props.evaluatedTask, evaluationStatus: toggleEvaluationStatus(props.evaluatedTask.evaluationStatus, evaluationStatus) }),
        onEvaluationChange: (evaluation: string) => saveEvaluation({ ...props.evaluatedTask, evaluation }),
    };

    // The evaluated task travels inside the evaluation task's query, so that is the cache entry updated optimistically.
    async function saveEvaluation(evaluatedTask: EvaluatedTaskViewModel) {
        const saveModel: TaskEvaluationSaveModel = { evaluation: evaluatedTask.evaluation, evaluationStatus: evaluatedTask.evaluationStatus };
        await optimisticUpdate.putOnQueueAndSetQueryData(
            { ...props.evaluationTask, evaluatedTask },
            getTaskQueryKey(props.evaluationTask.id),
            (model: TaskEvaluationSaveModel) => putTaskEvaluation(props.evaluatedTask.id, model),
            saveModel
        );
        patchCachedEvaluatedTask(saveModel);
    }

    // Patched rather than refetched, so an original task nobody has open costs no request per keystroke burst
    function patchCachedEvaluatedTask(saveModel: TaskEvaluationSaveModel) {
        queryClient.setQueryData<TaskViewModel | undefined>(
            getTaskQueryKey(props.evaluatedTask.id),
            (cachedTask) => cachedTask && { ...cachedTask, ...saveModel }
        );
    }

    return {
        evaluatedTaskUrl: getTaskManagementTaskDialogUrl(props.evaluatedTask.id),
        evaluationUpdater,
    };
}
